import type { IssueMetadata } from '../types/issue';

export type IssueHistoryEventType =
  | 'created'
  | 'status_changed'
  | 'assigned'
  | 'severity_changed'
  | 'action_linked'
  | 'action_completed'
  | 'project_linked'
  | 'workflow_linked'
  | 'goal_linked'
  | 'validation_started'
  | 'efficacy_check'
  | 'opportunity_linked'
  | 'reopened'
  | 'note'
  | 'mode_resolved';

export interface IssueHistoryEntry {
  id: string;
  type: IssueHistoryEventType;
  message: string;
  actor_id?: string;
  actor_name?: string;
  at: string;
  meta?: Record<string, unknown>;
}

const MAX_HISTORY = 50;

export function getIssueHistory(metadata?: IssueMetadata | null): IssueHistoryEntry[] {
  const history = (metadata?.history ?? []) as IssueHistoryEntry[];
  return [...history].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function getRecentIssueHistory(metadata?: IssueMetadata | null, limit = 5): IssueHistoryEntry[] {
  return getIssueHistory(metadata).slice(0, limit);
}

export function createHistoryEntry(
  type: IssueHistoryEventType,
  message: string,
  opts?: { actor_id?: string; actor_name?: string; meta?: Record<string, unknown> },
): IssueHistoryEntry {
  return {
    id: crypto.randomUUID(),
    type,
    message,
    at: new Date().toISOString(),
    ...opts,
  };
}

/** Eficácia e reabertura sustentam a regra de fechamento. O teto não pode apagá-los. */
export function isCanonicalHistoryEvent(entry: IssueHistoryEntry): boolean {
  if (entry.type === 'reopened') return true;
  if (entry.type !== 'efficacy_check') return false;
  const efficacy = entry.meta?.efficacy;
  return Boolean(efficacy && typeof efficacy === 'object');
}

export function appendToHistory(
  metadata: IssueMetadata | null | undefined,
  entry: IssueHistoryEntry,
): IssueMetadata {
  const base = metadata ?? {};
  const history = [...(base.history ?? []), entry];
  const protectedEntries = history.filter(isCanonicalHistoryEvent);
  const protectedIds = new Set(protectedEntries.map((item) => item.id));
  const others = history
    .filter((item) => !protectedIds.has(item.id))
    .sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime());
  const room = Math.max(0, MAX_HISTORY - protectedEntries.length);
  const kept = [...protectedEntries, ...others.slice(0, room)]
    .sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime());
  return { ...base, history: kept };
}

/**
 * Reabre sem apagar eficácia nem o motivo antigo de encerramento.
 * O motivo antigo fica no evento. O campo ativo é limpo para o próximo caso C.
 */
export function reopenIssueMetadata(
  metadata: IssueMetadata | null | undefined,
  input: { reason: string; actorId?: string; actorName?: string; at?: string },
): IssueMetadata {
  const reason = input.reason.trim();
  if (!reason) throw new Error('É necessário informar o motivo da reabertura.');
  const previous = metadata?.closure_reason;
  const base: IssueMetadata = { ...(metadata ?? {}) };
  delete base.closure_reason;
  const entry = createHistoryEntry('reopened', 'Problema reaberto', {
    actor_id: input.actorId,
    actor_name: input.actorName,
    meta: {
      reopen_reason: reason,
      previous_closure_reason: previous ?? null,
    },
  });
  if (input.at) entry.at = input.at;
  return appendToHistory(base, entry);
}
